import React, { useEffect, useRef } from "react";

export interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
}

export interface PopoverMenuProps {
  x: number;
  y: number;
  items: MenuItem[];
  onClose: () => void;
  /**
   * Align menu relative to trigger element
   * - "bottom-right": Menu appears below and aligned to right edge (default for ellipsis menus)
   * - "cursor": Menu appears at exact x,y coordinates (default for context menus)
   */
  align?: "bottom-right" | "cursor";
}

export function PopoverMenu({
  x,
  y,
  items,
  onClose,
  align = "cursor",
}: PopoverMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    // Add listeners with a small delay to prevent immediate closing
    setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }, 10);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Adjust position if menu would go off screen
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (align === "bottom-right") {
        // For ellipsis menus, position below and to the right of the trigger
        adjustedX = x - rect.width;
        adjustedY = y;
      }

      // Prevent menu from going off screen
      if (adjustedX + rect.width > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      if (adjustedX < 10) {
        adjustedX = 10;
      }

      if (adjustedY + rect.height > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      if (adjustedY < 10) {
        adjustedY = 10;
      }

      if (adjustedX !== x || adjustedY !== y || align === "bottom-right") {
        menuRef.current.style.left = `${adjustedX}px`;
        menuRef.current.style.top = `${adjustedY}px`;
      }
    }
  }, [x, y, align]);

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[200px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl shadow-gray-900/10 dark:shadow-black/30 py-1"
      style={{ left: x, top: y }}
      role="menu"
    >
      {items.map((item, index) => {
        if (item.separator) {
          return (
            <div
              key={`separator-${index}`}
              className="my-1 h-px bg-gray-200 dark:bg-gray-700"
            />
          );
        }

        return (
          <button
            key={index}
            onClick={() => {
              if (!item.disabled) {
                item.onClick();
                onClose();
              }
            }}
            disabled={item.disabled}
            className={`
              w-full flex items-center gap-3 px-4 py-2 text-sm text-left
              transition-colors
              ${
                item.disabled
                  ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                  : item.danger
                  ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              }
            `}
            role="menuitem"
          >
            {item.icon && (
              <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>
            )}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
